
import { IExtendedReport, IReport, ISummary } from '@/api/types/stats';
import SvgExtendedReport from '@/assets/icons/nav-bar/extended-report.svg';
import SvgPinFull from '@/assets/icons/pin-full.svg';
import SvgPin from '@/assets/icons/pin.svg';
import SvgScrollArrNext from '@/assets/icons/scroll-arr-next.svg';
import SvgScrollArrPrev from '@/assets/icons/scroll-arr-prev.svg';
import CardMaterial from '@/components/base/CardMaterial.vue';
import Pagination from '@/components/base/table/Pagination.vue';
import ReportLayout from '@/components/layout/ReportLayout.vue';
import SummaryRow from '@/components/statistics/extended-report/SummaryRow.vue';
import ReportTableFooter from '@/components/statistics/extended-report/TableFooter.vue';
import { REPORT_PAGINATION_PER_PAGE } from '@/configs/global';
import TableScroll from '@/directives/TableScroll';
import { ITableHeader } from "@/utils";
import { translateLang, translateMonth } from '@/utils/translate';
import isEqual from 'lodash-es/isEqual';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import VueRouter from 'vue-router';
import { Getter } from 'vuex-class';
import { useStatsCompareStore } from '../../../store_v3/StatsCompareStore';
import DropdownSortList from '@/components_v3/statistics/DropdownSortList.vue';
import type { SortSelectedItem, SortDropdownAlign } from '@/types/report-sort';
import { SORT_DROPDOWN_ALIGN, SORT_DROPDOWN_ALIGN_LEFT_MAX_INDEX, SORT_TYPE } from '@/types/report-sort';
import SvgOrangeLock from "@/assets/icons/oragne-lock.svg";

const { isNavigationFailure, NavigationFailureType } = VueRouter;

const PRIVATE_OFFER_ICON_COLUMN = '__private_offer_icon';

@Component({
    components: {
        DropdownSortList,
        ReportLayout,
        ReportTableFooter,
        SvgExtendedReport,
        Pagination,
        CardMaterial,
        SvgPin,
        SvgScrollArrNext,
        SvgScrollArrPrev,
        SvgPinFull,
        SummaryRow,
        SvgOrangeLock,
    },
    directives: { TableScroll },
})
export default class ExtendedReportTable extends Vue {
    @Prop({ required: true, default: { report: [], summary: [] } }) items!: IExtendedReport;
    @Prop({ required: true }) headers!: ITableHeader[];
    @Prop({ default: false }) isNotFound!: boolean;
    @Prop({ default: false }) loading!: boolean;
    @Prop({ default: false }) loadingExport!: boolean;
    @Prop({ default: '' }) search!: string;
    @Prop({ default: '' }) convertCurrency!: string;
    @Prop() sortByFromUrl!: string;

    @Getter('GET_FILTERS_PARAMS_OBJECT') getFiltersParamsObject;

    sortBy: string[] = [];
    sortDesc: boolean[] = [true];

    sortDropdownHeader: string | null = null;
    compareSortByColumn: Record<string, SortSelectedItem | null> = {};

    // для скрола
    scrollX = 0;
    pageCount = 0;
    itemsPerPage = REPORT_PAGINATION_PER_PAGE;
    pagination: { page: number } = { page: 1 };
    title = 'Расширенный отчет';
    isSticky = true;
    showFullSummary = false;
    canBeInactive = ['dm_offer_id', 'dm_offer_name', 'dm_offer_goal_id', 'dm_offer_goal_name'];
    private tableData:any[] = [];

    // virtual scroll
    rowHeight = 32;
    visibleRows = 42;
    scrollTop = 0;

    // compare
    compareStore = useStatsCompareStore();

    get isComparePeriod(): boolean {
        return this.compareStore.isComparePeriod;
    }

    colsWithMonoFont = [
        'mt_clicks_total',
        'mt_clicks_unique',
        'mt_leads_total',
        'mt_leads_approved',
        'mt_leads_pending',
        'mt_leads_rejected',
        'mt_payout_total',
        'mt_payout_pending',
        'mt_payout_approved',
        'mt_payout_rejected',
        'mt_ppl',
        'mt_revenue_total',
        'mt_revenue_pending',
        'mt_revenue_approved',
        'mt_revenue_rejected',
        'mt_rpl',
        'mt_profit',
        'mt_margin',
        'mt_cr_u',
        'mt_cr_t',
        'mt_ar',
        'mt_epc_u',
        'mt_epc_t',
        'mt_epc_adv',
        'mt_epl',
        'mt_bonus_amount',
        'mt_bonus_payout_approved',
        'mt_bonus_epc',
        'mt_bonus_epl',
        'mt_bonus_epc_t',
    ];

    get isScrollBtns(): boolean {
        if (this.$refs["extend-table"]) {
            // @ts-ignore
            const overWidth = this.$refs["extend-table"].$el.offsetWidth + 50 > document.body.clientWidth;
            return this.$props.items?.report && (this.$vuetify.breakpoint.lg || this.$vuetify.breakpoint.xl) && overWidth;
        } else {
            return true;
        }
    }

    isInactiveOffer(item: IReport, header: string): boolean {
        return item.dm_offer_status !== 'active' && this.canBeInactive.includes(header);
    }

    getRouteColumn(headerValue: string): any {
        return this.routes.find((i) => i.key === headerValue);
    }

    isRouteColumnChip(headerValue: string): boolean {
        const route = this.getRouteColumn(headerValue);
        return !!(route && route.isChip);
    }

    getRouteLocation(item: IReport, headerValue: string): { name: string; params: Record<string, string | number> } {
        const route = this.getRouteColumn(headerValue);
        if (!route) {
            return { name: '', params: {} };
        }
        return {
            name: route.routerName,
            params: route.getParams(item as any) as Record<string, string | number>,
        };
    }

    shouldRenderRouteLink(item: IReport, headerValue: string): boolean {
        const route = this.getRouteColumn(headerValue);
        if (!route) return false;
        const id = route.getParams(item as any)?.id;
        return id !== undefined && id !== null && id !== '';
    }

    formatRouteCell(item: IReport, headerValue: string): string {
        const route = this.getRouteColumn(headerValue);
        if (!route) return '';
        const direct = (item as any)[headerValue];
        if (direct !== undefined && direct !== null && direct !== '') {
            return String(direct);
        }
        const fromTemplate = route.template(item as any) as string | number | undefined | null;
        if (fromTemplate !== undefined && fromTemplate !== null && fromTemplate !== '') {
            return String(fromTemplate);
        }
        return '';
    }

    get displayHeaders(): ITableHeader[] {
        let list: ITableHeader[];

        const hasOfferColumn = this.headers.some(
            (h) => h.value === 'dm_offer_id' || h.value === 'dm_offer_name',
        );

        if (!hasOfferColumn) {
            list = this.headers;
        } else {
            const insertIndex = this.headers.findIndex(
                (h) => h.value === 'dm_offer_id' || h.value === 'dm_offer_name',
            );
            const iconHeader: ITableHeader = {
                text: '',
                value: PRIVATE_OFFER_ICON_COLUMN,
                sortable: false,
                width: '20px',
                align: 'center',
            };
            list = [
                ...this.headers.slice(0, insertIndex),
                iconHeader,
                ...this.headers.slice(insertIndex),
            ];
        }

        if (!this.isComparePeriod) {
            return list;
        }

        return list.map((h) =>
            h.sortable === false ? h : { ...h, sortable: false },
        );
    }

    showPrivateOfferIcon(headerValue: string, item: IReport): boolean {
        if (!item.dm_offer_private) return false;
        if (headerValue === 'dm_offer_id') return true;
        if (headerValue === 'dm_offer_name') {
            return !this.headers.some(h => h.value === 'dm_offer_id');
        }
        return false;
    }

    isPrivateOfferIconColumn(headerValue: string): boolean {
        return headerValue === PRIVATE_OFFER_ICON_COLUMN;
    }

    scrollXNext(): void {
        this.scrollX += 250;
        // @ts-ignore
        this.$refs["vs"].scrollTo({ x: `${this.scrollX}` }, 500);
    }

    scrollXPrev(): void {
        if (this.scrollX >= 250) {
            this.scrollX -= 250;
        } else {
            this.scrollX = 0;
        }
        // @ts-ignore
        this.$refs["vs"].scrollTo({ x: `${this.scrollX}` }, 500);
    }

    handleOptionsUpdate({ sortBy, sortDesc }: {sortBy: string[], sortDesc: boolean[]}): void {
        if (this.isComparePeriod) {
            return;
        }
        if (
            sortBy[0] === this.sortBy[0] &&
            sortDesc[0] === this.sortDesc[0]
        ) return;

        if (sortBy[0] && sortDesc[0] === false) {
            this.sortDesc = [true];
            return;
        }

        if (sortBy.length === 0 && sortDesc.length === 0) {
            this.sortBy = [this.sortBy[0]];
            this.sortDesc = [false];
            return;
        }

        if (sortBy[0] && sortDesc[0] === true) {
            this.sortBy = [];
            this.sortDesc = [];
            return;
        }
    }

    isMonoFont(val: string): boolean {
        return this.colsWithMonoFont.includes(val);
    }

    isNumber(val: string): boolean {
        return this.numberFormatting.some(i => i.key === val);
    }

    getNumberFormatting(val: number | undefined): string {
        if (val === undefined) {
            return '0.00';
        }
        return Number(val).toLocaleString('ru-RU', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        }) || '0.00';
    }

    numberFormatting = [
        { slot: 'item.mt_profit', key: 'mt_profit' },
        { slot: 'item.mt_margin', key: 'mt_margin' },
        { slot: 'item.mt_payout_total', key: 'mt_payout_total' },
        { slot: 'item.mt_payout_pending', key: 'mt_payout_pending' },
        { slot: 'item.mt_payout_approved', key: 'mt_payout_approved' },
        { slot: 'item.mt_payout_rejected', key: 'mt_payout_rejected' },
        { slot: 'item.mt_ppl', key: 'mt_revenue_total' },
        { slot: 'item.mt_revenue_pending', key: 'mt_revenue_pending' },
        { slot: 'item.mt_revenue_approved', key: 'mt_revenue_approved' },
        { slot: 'item.mt_revenue_rejected', key: 'mt_revenue_rejected' },
        { slot: 'item.mt_rpl', key: 'mt_rpl' },
        { slot: 'item.mt_cr_u', key: 'mt_cr_u' },
        { slot: 'item.mt_cr_t', key: 'mt_cr_t' },
        { slot: 'item.mt_ar', key: 'mt_ar' },
        { slot: 'item.mt_epc_u', key: 'mt_epc_u' },
        { slot: 'item.mt_epc_t', key: 'mt_epc_t' },
        { slot: 'item.mt_epc_adv', key: 'mt_epc_adv' },
        { slot: 'item.mt_epl', key: 'mt_epl' },
        { slot: 'item.mt_bonus_amount', key: 'mt_bonus_amount' },
        { slot: 'item.mt_bonus_payout_approved', key: 'mt_bonus_payout_approved' },
        { slot: 'item.mt_bonus_epc', key: 'mt_bonus_epc' },
        { slot: 'item.mt_bonus_epc_t', key: 'mt_bonus_epc_t' },
        { slot: 'mt_bonus_epl', key: 'mt_bonus_epl' },
    ];

    // для отображения роутов в таблице
    routes = [
        {
            getParams(item: {dm_offer_id: string}): {id: string} {
                return {
                    id: item.dm_offer_id!,
                };
            },
            routerName: 'offer',
            slot: 'item.dm_offer_goal_id',
            text: 'dm_offer_goal_id',
            key: 'dm_offer_goal_id',
            isChip: true,
            template: (item: {dm_offer_goal_id: string}): string => item.dm_offer_goal_id,
        },
        {
            getParams(item: {dm_offer_id: string}): {id: string} {
                return {
                    id: item.dm_offer_id!,
                };
            },
            routerName: 'offer',
            slot: 'item.dm_offer_goal_name',
            text: 'dm_offer_goal_name',
            key: 'dm_offer_goal_name',
            template: (item: {dm_offer_goal_name: string}): string => item.dm_offer_goal_name,
        },
        {
            getParams(item: {dm_offer_id: string}): {id: string} {
                return {
                    id: item.dm_offer_id!,
                };
            },
            routerName: 'offer',
            slot: 'item.dm_offer_name',
            text: 'dm_offer_name',
            key: 'dm_offer_name',
            template: (item: {dm_offer_name: string}): string => item.dm_offer_name,
        },
        {
            getParams(item: {dm_offer_id: string}): {id: string} {
                return {
                    id: item.dm_offer_id!,
                };
            },
            routerName: 'offer',
            slot: 'item.dm_offer_id',
            text: 'dm_offer_id',
            key: 'dm_offer_id',
            isChip: true,
            template: (item: {dm_offer_id: string}): string => item.dm_offer_id,
        },
        {
            template: (item: {dm_affiliate_name: string | null}): string => item.dm_affiliate_name || '',
            getParams(item: {dm_affiliate_id: string}): {id: string} {
                return {
                    id: item.dm_affiliate_id!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_affiliate_name',
            text: 'dm_affiliate_name',
            key: 'dm_affiliate_name',
        },
        {
            getParams(item: {dm_affiliate_id: string}): {id: string} {
                return {
                    id: item.dm_affiliate_id!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_affiliate_id',
            text: 'dm_affiliate_id',
            key: 'dm_affiliate_id',
            isChip: true,
            template: (item: {dm_affiliate_id: string}): string => item.dm_affiliate_id,
        },
        {
            getParams(item: {dm_buffer_account_id: string | number}): {id: string | number} {
                return {
                    id: item.dm_buffer_account_id!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_buffer_account_id',
            text: 'dm_buffer_account_id',
            key: 'dm_buffer_account_id',
            isChip: true,
            template: (item: {dm_buffer_account_id:  string | number}):  string | number => item.dm_buffer_account_id,
        },
        {
            template: (item: {dm_buffer_account_name: string | null}): string => item.dm_buffer_account_name || '',
            getParams(item: {dm_buffer_account_id: string}): {id: string} {
                return {
                    id: item.dm_buffer_account_id!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_buffer_account_name',
            text: 'dm_buffer_account_name',
            key: 'dm_buffer_account_name',
        },
        {
            template: (item: {dm_master_account_name: string | null}): string => item.dm_master_account_name || '',
            getParams(item: {dm_master_account: string}): {id: string} {
                return {
                    id: item.dm_master_account!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_master_account_name',
            text: 'dm_master_account_name',
            key: 'dm_master_account_name',
        },
        {
            getParams(item: {dm_master_account: string | number}): {id: string | number} {
                return {
                    id: item.dm_master_account!,
                };
            },
            routerName: 'affiliate',
            slot: 'item.dm_master_account',
            text: 'dm_master_account',
            key: 'dm_master_account',
            isChip: true,
            template: (item: {dm_master_account:  string | number}):  string | number => item.dm_master_account,
        },
        {
            getParams(item: { dm_advertiser_id: string}): {id: string} {
                return {
                    id: item.dm_advertiser_id!,
                };
            },
            routerName: 'advertiser',
            slot: 'item.dm_advertiser_name',
            text: 'dm_advertiser_name',
            key: 'dm_advertiser_name',
            isChip: false,
            template: (item: {dm_advertiser_name: string}): string => item.dm_advertiser_name,
        },
    ];

    uniqueData = [
        {
            slot: 'item.dm_affiliate_manager_name',
            key: 'dm_affiliate_manager_name',
            template: (item: any): string => item.dm_affiliate_manager_name || '',
        },
        {
            slot: 'item.dm_buffer_account_used',
            key: 'dm_buffer_account_used',
            template: (item: any): string => item.dm_buffer_account_used ? 'Да' : 'Нет',
        },
        {
            slot: 'item.dm_advertiser_manager_name',
            key: 'dm_advertiser_manager_name',
            template: (item: any): string => item.dm_advertiser_manager_name || '',
        },
        {
            slot: 'item.dm_offer_country',
            key: 'dm_offer_country',
            template: (item: any): string => item.dm_offer_country ? item.dm_offer_country.join(', ') : '',
        },
        {
            slot: 'item.dm_lead_date_month',
            key: 'dm_lead_date_month',
            template: (item: any): string => translateMonth(item.dm_lead_date_month),
        },
        {
            slot: 'item.dm_affiliate_traffic_source_id',
            key: 'dm_affiliate_traffic_source_id',
            template: (item: any): string => item.dm_affiliate_traffic_source_id ? item.dm_affiliate_traffic_source_id.join(', ') : '',
        },
        {
            slot: 'item.dm_master_traffic_sources',
            key: 'dm_master_traffic_sources',
            template: (item: any): string => item.dm_master_traffic_sources ? item.dm_master_traffic_sources.join(', ') : '',
        },
        {
            slot: 'item.dm_affiliate_traffic_source_name',
            key: 'dm_affiliate_traffic_source_name',
            template: (item: any): string => item.dm_affiliate_traffic_source_name ? item.dm_affiliate_traffic_source_name.join(', ') : '',
        },
        {
            slot: 'item.dm_master_tag',
            key: 'dm_master_tag',
            template: (item: any): string => item.dm_master_tag ? item.dm_master_tag.join(', ') : '',
        },
        {
            slot: 'item.dm_offer_vertical',
            key: 'dm_offer_vertical',
            template: (item: any): string => item.dm_offer_vertical ? item.dm_offer_vertical.join(', ') : '',
        },
        {
            slot: 'item.dm_offer_tag',
            key: 'dm_offer_tag',
            template: (item: any): string => item.dm_offer_tag ? item.dm_offer_tag.join(', ') : '',
        },
        {
            slot: 'item.dm_offer_product',
            key: 'dm_offer_product',
            template: (item: any): string => item.dm_offer_product ? item.dm_offer_product.join(', ') : '',
        },
        {
            slot: 'item.dm_offer_currency',
            key: 'dm_offer_currency',
            template: (item: any): string => item.dm_offer_currency,
        },
        {
            slot: 'item.dm_master_contract_type',
            key: 'dm_master_contract_type',
            template: (item: any): string => item.dm_master_contract_type!,
        },
        {
            slot: 'item.dm_master_contract_type_marking',
            key: 'dm_master_contract_type_marking',
            template: (item: any): string => item.dm_master_contract_type_marking === null ? '' : item.dm_master_contract_type_marking ? 'Да' : 'Нет',
        },
        {
            slot: 'item.dm_master_verticals',
            key: 'dm_master_verticals',
            template: (item: any): string => {
                const value: string[] | null = item.dm_master_verticals;

                if (value !== null) {
                    return value[0] === 'finance' ? 'Финансы' : 'E-Commerce';
                }

                return '';
            },
        },
        {
            slot: 'item.dm_master_cabinet_language',
            key: 'dm_master_cabinet_language',
            template: (item: any): string => (item.dm_master_cabinet_language ? translateLang(String(item.dm_master_cabinet_language)) : ''),
        },
        {
            slot: 'item.dm_affiliate_cabinet_language',
            key: 'dm_affiliate_cabinet_language',
            template: (item: any): string => (item.dm_affiliate_cabinet_language ? translateLang(String(item.dm_affiliate_cabinet_language)) : ''),
        },
        {
            slot: 'item.dm_master_language',
            key: 'dm_master_language',
            template: (item: any): string => (item.dm_master_language ? translateLang(String(item.dm_master_language)) : ''),
        },
        {
            slot: 'item.dm_affiliate_language',
            key: 'dm_affiliate_language',
            template: (item: any): string => (item.dm_affiliate_language ? translateLang(String(item.dm_affiliate_language)) : ''),
        },
    ];

    getClassForCurrency(key: string, item?: any): string {
        if (key !== 'dm_offer_currency') {
            return '';
        }
        if (item) {
            return item.dm_offer_currency === this.convertCurrency ? 'currency-conversion-cell' : '';
        }
        return !!this.convertCurrency ? 'currency-conversion-head' : '';
    }

    get summaryTableItems(): ISummary[] {
        const order = ['RUB', 'KZT', 'EUR', 'USD'];

        if (!this.$props.items?.summary){
            return [];
        }else{
            const result = this.$props.items?.summary.sort((a, b) => {
                const idxA = order.indexOf(a.dm_offer_currency);
                const idxB = order.indexOf(b.dm_offer_currency);

                const rankA = idxA === -1 ? order.length : idxA;
                const rankB = idxB === -1 ? order.length : idxB;

                return rankA - rankB;
            });

            return result;
        }
    }

    get total(): number {
        return this.$props.items.report?.length;
    }

    updateSortInQuery(): void {
        if (
            this.sortBy?.[0] === this.sortByFromUrl &&
            this.sortDesc?.[0]?.toString() === this.$route.query?.sort_desc
        ) return;

        const query: any = { ...this.$route.query };

        if (this.sortBy?.[0]) {
            query.sort_by = this.sortBy[0];
            query.sort_desc = this.sortDesc?.[0];
        } else {
            delete query.sort_by;
            delete query.sort_desc;
        }

        this.$router
            .push({ name: this.$router.currentRoute.name || '', query })
            .catch((e) => {
                if (!isNavigationFailure(e, NavigationFailureType.duplicated)) {
                    throw e;
                }
            });
    }

    @Watch('getFiltersParamsObject', { deep: true })
    changeSortBy(value: any, oldValue: any): void {
        if (isEqual(value, oldValue)) return;

        const columns = this.getFiltersParamsObject?.columns;
        if (!(columns && Array.isArray(columns)) || columns.includes(this.sortBy[0])) {
            return;
        }
        // иначе ставим дефолтную сортировку по дате или кликам
        if (columns.includes('dm_lead_date')) {
            this.sortBy = [this.getFiltersParamsObject.group] || ['dm_lead_date'];
            return;
        }
        if (columns.includes('mt_clicks_total')) {
            this.sortBy = ['mt_clicks_total'];
        }
        // если удалён столбец с сортировкой, отменяем сортировку по нему
        if (columns.length && !columns.includes(this.sortBy?.[0])) {
            this.sortBy = [this.getFiltersParamsObject.columns[0]];
        }
    }

    @Watch('items', { deep: true })
    setItems(): void {
        this.updateTableData();
    }

    @Watch('sortBy')
    @Watch('sortDesc')
    @Watch('search')
    handleSortChange(): void {
        setTimeout(() => {
            this.updateTableData();
            this.updateSortInQuery();
        });
    }

    @Watch('sortByFromUrl', { immediate: true })
    setSortByQuery(value: string): void {
        this.sortBy = [value];
    }

    @Watch('isComparePeriod')
    syncCompareSort(isCompare: boolean): void {
        if (!isCompare) {
            return;
        }
        const col = this.sortBy?.[0];
        if (!col) {
            this.compareSortByColumn = {};
            return;
        }
        this.compareSortByColumn = {
            [col]: {
                column: col,
                sortType: SORT_TYPE.Current,
                direction: this.sortDesc[0] ? 'desc' : 'asc',
            },
        };
        this.updateTableData();
    }

    get startIndex(): number {
        return Math.floor(this.scrollTop / this.rowHeight);
    }

    get endIndex(): number {
        return Math.min(this.startIndex + this.visibleRows, this.tableData.length);
    }

    get visibleItems(): any[] {
        return this.tableData.slice(this.startIndex, this.endIndex);
    }

    get offsetTop(): number {
        return this.startIndex * this.rowHeight;
    }

    get offsetBottom(): number {
        return (this.tableData.length - this.endIndex) * this.rowHeight;
    }


    onScroll(event: Event): void {
        const target = event.target as HTMLElement;
        this.scrollTop = target.scrollTop;
    }

    sortDropdownAlign(columnIndex: number): SortDropdownAlign {
        const last = this.displayHeaders.length - 1;
        if (columnIndex <= SORT_DROPDOWN_ALIGN_LEFT_MAX_INDEX) {
            return SORT_DROPDOWN_ALIGN.Start;
        }
        if (columnIndex === last) {
            return SORT_DROPDOWN_ALIGN.End;
        }
        return SORT_DROPDOWN_ALIGN.Center;
    }

    onSortSelect(item: SortSelectedItem): void {
        if (item.sortType === SORT_TYPE.None) {
            this.compareSortByColumn = {};
            this.sortBy = [];
            this.sortDesc = [];
        } else {
            this.compareSortByColumn = { [item.column]: item };
            this.sortBy = [item.column];
            this.sortDesc = [item.direction === 'desc'];
        }
        this.sortDropdownHeader = null;
        this.updateTableData();
    }

    updateTableData(): void {
        if (!this.$props.items.report?.length) {
            this.tableData = [];
            return;
        }

        const filteredData = this.search
            ? this.$props.items.report.filter((item) =>
                this.headers.some((header) => {
                    const cellValue = item[header.value];
                    return cellValue && cellValue.toString().toLowerCase().includes(this.search.toLowerCase());
                }),
            )
            : [...this.$props.items.report];

        if (this.sortBy[0]) {
            this.tableData = [...filteredData].sort((a, b) => {
                return this.compareValues(a, b);
            });
        } else {
            this.tableData = filteredData;
        }
    }

    compareValues(a: any, b: any): number {
        const key = this.sortBy[0];
        const order = this.sortDesc[0] ? -1 : 1;
        const arrayCols = ['dm_affiliate_traffic_source_name', 'dm_master_traffic_sources', 'dm_offer_vertical', 'dm_offer_product', 'dm_master_tag'];
        const numCols = ['dm_master_contract_type_marking'];

        let aValue = a[key] ?? '';
        let bValue = b[key] ?? '';

        if (this.isComparePeriod && this.colsWithMonoFont.includes(key)) {
            const sortItem = this.compareSortByColumn[key];
            const cellA = a[key];
            const cellB = b[key];
            if (!Array.isArray(cellA) || !Array.isArray(cellB)) {
                aValue = 0;
                bValue = 0;
            } else {
                const st = sortItem?.sortType;
                if (st === SORT_TYPE.Diff) {
                    aValue = Number(cellA[2] ?? 0);
                    bValue = Number(cellB[2] ?? 0);
                } else if (st === SORT_TYPE.Prev) {
                    aValue = Number(cellA[1] ?? 0);
                    bValue = Number(cellB[1] ?? 0);
                } else {
                    aValue = Number(cellA[0] ?? 0);
                    bValue = Number(cellB[0] ?? 0);
                }
            }
        }

        if (numCols.includes(key)) {
            aValue = a[key] ?? -1;
            bValue = b[key] ?? -1;
        }

        if (arrayCols.includes(key)) {
            aValue = Array.isArray(aValue) ? aValue.join('') : '';
            bValue = Array.isArray(bValue) ? bValue.join('') : '';
        }

        if (typeof aValue === 'string' || typeof bValue === 'string') {
            return aValue.localeCompare(bValue) * order;
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return (aValue - bValue) * order;
        } else {
            return String(aValue).localeCompare(String(bValue)) * order;
        }
    }
}
